import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { QUESTS } from '@yuan-math/content';
import type { LearnerProfile } from '@yuan-math/learning';
import {
  blockingQuest,
  questProgress,
  questsForKingdom,
  type QuestDef,
  type QuestKind,
  type QuestProgress,
} from '@yuan-math/world';

/**
 * A kingdom's quest board (docs/rpg.md §13).
 *
 * The board is a *list of doors*, and every door is open: a card links to its
 * quest page whatever state it is in, and a quest page links onward to lessons
 * and practice that were already reachable from the ordinary routes. "Locked"
 * here means the story has not reached that quest yet
 * (docs/rpg-implementation-plan.md §0.1) — which is why a locked card still
 * shows its progress and still names the quest to go and do instead, rather
 * than being a grey rectangle with a padlock and no next move.
 *
 * Every state is a word, an icon *and* a colour, never colour alone (CLAUDE.md).
 */

/** How each kind announces itself. `review` is a side quest that revisits. */
export const QUEST_KIND: Record<QuestKind, { label: string; icon: string }> = {
  main: { label: 'Main quest', icon: '⚔️' },
  side: { label: 'Side quest', icon: '🧭' },
  review: { label: 'Review quest', icon: '🔁' },
};

/**
 * The state chip.
 *
 * `blocker` is the quest holding a locked one shut — derived once by the caller
 * from `blockingQuest`, so the chip and the card cannot disagree about which
 * quest to name.
 */
export function QuestStateChip({
  progress,
  blocker,
  id,
}: {
  progress: QuestProgress;
  blocker?: QuestDef | undefined;
  /**
   * Set when something else on the page points at this chip with
   * `aria-describedby` — the board's cards do, so the state is announced after
   * the quest's name rather than in front of it.
   */
  id?: string;
}): JSX.Element {
  const { state, done, total } = progress;
  const copy: Record<QuestProgress['state'], { icon: string; text: string }> = {
    // Named, not merely refused: "locked" on its own is the one thing a
    // student cannot act on.
    locked: { icon: '🔒', text: blocker ? `Finish ${blocker.title} first` : 'Not started yet' },
    available: { icon: '📜', text: 'Available' },
    accepted: { icon: '⏳', text: `In progress — ${done}/${total}` },
    complete: { icon: '🎁', text: 'Reward waiting' },
    claimed: { icon: '✅', text: 'Claimed' },
  };
  const { icon, text } = copy[state];

  return (
    <span className={`quest-chip quest-chip--${state}`} {...(id ? { id } : {})}>
      <span className="quest-chip__icon" aria-hidden="true">
        {icon}
      </span>
      {text}
    </span>
  );
}

/** The kind badge: an icon and the word, in the kind's own colour. */
export function QuestKindBadge({ kind }: { kind: QuestKind }): JSX.Element {
  const { label, icon } = QUEST_KIND[kind];
  return (
    <span className={`quest-kind quest-kind--${kind}`}>
      <span className="quest-kind__icon" aria-hidden="true">
        {icon}
      </span>
      {label}
    </span>
  );
}

/**
 * The board itself: the main chain first and in order, then the side and review
 * quests under it — which is exactly what `questsForKingdom` returns, so the
 * ordering is the engine's opinion rather than this file's.
 */
export function QuestBoard({
  kingdomId,
  kingdomName,
  profile,
  now,
}: {
  kingdomId: string;
  kingdomName: string;
  profile: LearnerProfile | null;
  now: number;
}): JSX.Element {
  const quests = questsForKingdom(QUESTS, kingdomId);

  // A kingdom whose story has not been written yet. Its mathematics is all on
  // this page regardless, which is the point worth making.
  if (quests.length === 0 || !profile) {
    return (
      <p className="kingdom-quests__note">
        The quests of {kingdomName} are still being written; when they arrive they will be posted here. Every region
        and every lesson above is open in the meantime.
      </p>
    );
  }

  return (
    <ol className="quest-board">
      {quests.map((quest) => {
        const progress = questProgress(quest, QUESTS, profile, now);
        const blocker = blockingQuest(quest, QUESTS, profile);
        // The hook: the first beat of the story, which is written as the
        // problem the world has. One line, and the rest is on the quest page.
        const hook = quest.story[0]?.text ?? '';

        return (
          <li key={quest.id}>
            {/*
             * The whole card is the link, and it stands on the same coloured
             * bottom edge every other control on the site does — a quest is one
             * thing to press, not a title with decoration around it. The
             * `aria-label` keeps the announced *name* to the quest's own title:
             * a link read out as "Main quest available The Lantern Count Dusk
             * comes early…" is a worse way to hear a board of eight.
             *
             * The state is not lost by that, it is moved: `aria-describedby`
             * points at the chip, so a screen reader reads the title and then
             * "Reward waiting" — the order a sighted reader takes them in, and
             * the half a keyboard user most needs before deciding to press.
             */}
            <Link
              to={`/adventure/quests/${quest.id}`}
              aria-label={quest.title}
              aria-describedby={`quest-state-${quest.id}`}
              className={`card card--pressable quest-card quest-card--${progress.state}`}
            >
              <span className="quest-card__head">
                <QuestKindBadge kind={quest.kind} />
                <QuestStateChip progress={progress} blocker={blocker} id={`quest-state-${quest.id}`} />
              </span>
              <h3 className="quest-card__title">{quest.title}</h3>
              <span className="quest-card__hook">{hook}</span>
              <span className="quest-card__reward">
                <span aria-hidden="true">🎁</span> +{quest.reward.xp} XP · +{quest.reward.gold} gold
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}
